/**
 * Marketing attribution for the Gatsby sites (sites/RFC-004 §9.1–9.3, §9.5, §10).
 *
 * Keeps a first-touch (`ft`) and last-touch (`lt`) record of where a visitor came
 * from, in a first-party cookie (`hunto_attr`, parent domain when on *.hunto.ai)
 * plus a localStorage copy, and carries it into links to the Autopilot app and
 * into lead forms. Events go to `window.dataLayer` for GTM; GA4 tags live in GTM.
 *
 * Rules:
 * - `ft` is written only when absent; `lt` when a visit arrives with UTM or a
 *   click ID, or from an external referrer.
 * - No personal data: `track()` drops keys and values that look like an email,
 *   name, phone or domain the visitor typed.
 * - All values are untrusted input: lowercased where the convention says so and
 *   length-limited.
 * - Storage is best-effort; every access is wrapped in try/catch.
 *
 * Sister sites (host-only cookie) also pass `decorate: (u) => isHunto(u) ? referralUrl(u.href, "tikaj") : null`.
 *
 * Usage (gatsby-browser):
 *   import { captureAttribution, installLinkTracking } from "./gatsby-ui/lib/attribution"
 *   export const onClientEntry = () => installLinkTracking({ decorateHosts: ["autopilot.hunto.ai"] })
 *   export const onRouteUpdate = () => captureAttribution()
 */

/**
 * Write the cookie and localStorage copy only when analytics consent is granted.
 * There is no consent banner yet (2026-10-08), so this is off: we write unless the
 * page carries an explicit Consent Mode "denied" for analytics_storage. Turn it on
 * once a banner sets Consent Mode defaults (required before EEA/UK marketing).
 */
export const ATTRIBUTION_REQUIRES_CONSENT = false

export const ATTRIBUTION_COOKIE = "hunto_attr"
const MAX_AGE_SECONDS = 180 * 24 * 60 * 60
const MAX_LEN = 100
const MAX_PATH_LEN = 200

export interface Touch {
  source: string
  medium: string
  campaign: string
  content: string
  term: string
  gclid: string
  msclkid: string
  li_fat_id: string
  referrer: string
  landing: string
  ts: string
  /** How the touch was derived: from UTM, a click ID, a referrer, or direct. */
  via: "utm" | "click" | "referrer" | "direct"
}

export interface Attribution {
  ft?: Touch
  lt?: Touch
  site: string
}

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const
const CLICK_KEYS = ["gclid", "msclkid", "li_fat_id"] as const

/** Search engines whose referrer means `<engine>/organic`. Matched on the host. */
const SEARCH_ENGINES: [RegExp, string][] = [
  [/(^|\.)google\.[a-z.]+$/, "google"],
  [/(^|\.)bing\.com$/, "bing"],
  [/(^|\.)duckduckgo\.com$/, "duckduckgo"],
  [/(^|\.)search\.yahoo\.[a-z.]+$|(^|\.)yahoo\.[a-z.]+$/, "yahoo"],
  [/(^|\.)yandex\.[a-z.]+$/, "yandex"],
  [/(^|\.)baidu\.com$/, "baidu"],
  [/(^|\.)ecosia\.org$/, "ecosia"],
  [/(^|\.)search\.brave\.com$/, "brave"],
  [/(^|\.)naver\.com$/, "naver"],
  [/(^|\.)startpage\.com$/, "startpage"],
  [/(^|\.)qwant\.com$/, "qwant"],
  [/(^|\.)seznam\.cz$/, "seznam"],
]

/** Registrable roots treated as "our own site": referrers from these are not a new touch. */
const OWN_ROOTS = ["hunto.ai", "tikaj.com", "phishgrid.com"]

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined"

const clip = (v: unknown, n = MAX_LEN): string =>
  typeof v === "string" ? v.replace(/[\u0000-\u001f<>"]/g, "").trim().slice(0, n) : ""

const lc = (v: unknown, n = MAX_LEN) => clip(v, n).toLowerCase()

const stripWww = (h: string) => h.replace(/^www\./, "")

/** Root of the current site among OWN_ROOTS, or the full host. */
function siteRoot(host: string): string {
  const h = host.toLowerCase()
  return OWN_ROOTS.find((r) => h === r || h.endsWith("." + r)) ?? stripWww(h)
}

function isOwnHost(host: string): boolean {
  const h = host.toLowerCase()
  const here = siteRoot(window.location.hostname)
  return h === here || h.endsWith("." + here) || stripWww(h) === stripWww(window.location.hostname)
}

/** Cookie domain: `.hunto.ai` on any *.hunto.ai host, otherwise host-only. */
function cookieDomain(): string | undefined {
  const h = window.location.hostname.toLowerCase()
  return h === "hunto.ai" || h.endsWith(".hunto.ai") ? ".hunto.ai" : undefined
}

// ---------------------------------------------------------------- consent

/**
 * True when analytics storage may be written. Reads Google Consent Mode from
 * GTM's state (`google_tag_data.ics`) and from `consent` commands in the
 * dataLayer. With no consent signal at all, falls back to
 * `!ATTRIBUTION_REQUIRES_CONSENT`.
 */
export function analyticsConsentGranted(): boolean {
  if (!isBrowser()) return false
  try {
    const ics = (window as any).google_tag_data?.ics?.entries?.analytics_storage
    if (ics && (typeof ics.update === "boolean" || typeof ics.default === "boolean")) {
      return typeof ics.update === "boolean" ? ics.update : ics.default
    }
  } catch {
    /* ignore */
  }
  try {
    const dl: any[] = (window as any).dataLayer || []
    for (let i = dl.length - 1; i >= 0; i--) {
      const e = dl[i]
      if (e && e[0] === "consent" && e[2] && typeof e[2].analytics_storage === "string") {
        return e[2].analytics_storage === "granted"
      }
    }
  } catch {
    /* ignore */
  }
  return !ATTRIBUTION_REQUIRES_CONSENT
}

// ---------------------------------------------------------------- storage

function readCookie(): string | null {
  try {
    const m = document.cookie.match(new RegExp("(?:^|; )" + ATTRIBUTION_COOKIE + "=([^;]*)"))
    return m ? decodeURIComponent(m[1]) : null
  } catch {
    return null
  }
}

function sanitizeTouch(t: any): Touch | undefined {
  if (!t || typeof t !== "object") return undefined
  const via = ["utm", "click", "referrer", "direct"].includes(t.via) ? t.via : "direct"
  return {
    source: lc(t.source),
    medium: lc(t.medium),
    campaign: lc(t.campaign),
    content: lc(t.content),
    term: clip(t.term),
    gclid: clip(t.gclid, 200),
    msclkid: clip(t.msclkid, 200),
    li_fat_id: clip(t.li_fat_id, 200),
    referrer: clip(t.referrer, MAX_PATH_LEN),
    landing: clip(t.landing, MAX_PATH_LEN),
    ts: clip(t.ts, 30),
    via,
  }
}

function parse(raw: string | null): Attribution | null {
  if (!raw) return null
  try {
    const o = JSON.parse(raw)
    if (!o || typeof o !== "object") return null
    return { ft: sanitizeTouch(o.ft), lt: sanitizeTouch(o.lt), site: clip(o.site, 50) }
  } catch {
    return null
  }
}

/** Drop empty fields so the cookie stays small. */
function compact(t?: Touch): Partial<Touch> | undefined {
  if (!t) return undefined
  const out: Partial<Touch> = {}
  for (const [k, v] of Object.entries(t)) if (v) (out as any)[k] = v
  return out
}

function write(a: Attribution): void {
  if (!analyticsConsentGranted()) return
  const json = JSON.stringify({ ft: compact(a.ft), lt: compact(a.lt), site: a.site })
  try {
    const domain = cookieDomain()
    const secure = window.location.protocol === "https:" ? "; Secure" : ""
    document.cookie =
      `${ATTRIBUTION_COOKIE}=${encodeURIComponent(json)}; Max-Age=${MAX_AGE_SECONDS}; Path=/` +
      (domain ? `; Domain=${domain}` : "") +
      `; SameSite=Lax${secure}`
  } catch {
    /* ignore */
  }
  try {
    window.localStorage.setItem(ATTRIBUTION_COOKIE, json)
  } catch {
    /* ignore */
  }
}

/** The stored attribution record (cookie first, then the localStorage copy). */
export function getAttribution(): Attribution | null {
  if (!isBrowser()) return null
  const fromCookie = parse(readCookie())
  if (fromCookie?.ft) return fromCookie
  try {
    return parse(window.localStorage.getItem(ATTRIBUTION_COOKIE))
  } catch {
    return fromCookie
  }
}

// ---------------------------------------------------------------- capture

function emptyTouch(landing: string, via: Touch["via"]): Touch {
  return {
    source: "", medium: "", campaign: "", content: "", term: "",
    gclid: "", msclkid: "", li_fat_id: "", referrer: "",
    landing: clip(landing, MAX_PATH_LEN), ts: new Date().toISOString().slice(0, 19) + "Z", via,
  }
}

/** Derive `<engine>/organic`, `<host>/referral` or null (own site / no referrer). */
function fromReferrer(referrer: string): { source: string; medium: string; host: string } | null {
  if (!referrer) return null
  let host: string
  try {
    host = new URL(referrer).hostname.toLowerCase()
  } catch {
    return null
  }
  if (!host || isOwnHost(host)) return null
  const engine = SEARCH_ENGINES.find(([re]) => re.test(host))
  if (engine) return { source: engine[1], medium: "organic", host }
  return { source: stripWww(host), medium: "referral", host }
}

/** Build the touch this page view represents, or null when it is not a new touch. */
function touchFromVisit(url: URL, referrer: string, considerReferrer: boolean): Touch | null {
  const p = url.searchParams
  const landing = url.pathname
  const ref = considerReferrer ? fromReferrer(referrer) : null
  const hasUtm = UTM_KEYS.some((k) => p.get(k))
  const hasClick = CLICK_KEYS.some((k) => p.get(k))

  if (hasUtm || hasClick) {
    const t = emptyTouch(landing, hasUtm ? "utm" : "click")
    t.source = lc(p.get("utm_source"))
    t.medium = lc(p.get("utm_medium"))
    t.campaign = lc(p.get("utm_campaign"))
    t.content = lc(p.get("utm_content"))
    t.term = clip(p.get("utm_term"))
    t.gclid = clip(p.get("gclid"), 200)
    t.msclkid = clip(p.get("msclkid"), 200)
    t.li_fat_id = clip(p.get("li_fat_id"), 200)
    t.referrer = ref ? clip(ref.host, MAX_PATH_LEN) : ""
    // Click IDs without UTM: name the network, like GA4 does.
    if (!t.source) t.source = t.gclid ? "google" : t.msclkid ? "bing" : t.li_fat_id ? "linkedin" : ref?.source ?? "(not set)"
    if (!t.medium) t.medium = hasClick ? "cpc" : ref?.medium ?? "(not set)"
    return t
  }
  if (ref) {
    const t = emptyTouch(landing, "referrer")
    t.source = ref.source
    t.medium = ref.medium
    t.referrer = clip(ref.host, MAX_PATH_LEN)
    return t
  }
  return null
}

let lastHandledHref = ""
let referrerHandled = false

/**
 * Record this page view. Call on every route change (gatsby `onRouteUpdate`).
 * The referrer is considered only once per page load (it does not change on
 * client-side navigation); URL parameters are considered on every new URL.
 */
export function captureAttribution(): Attribution | null {
  if (!isBrowser()) return null
  try {
    const href = window.location.href
    if (href === lastHandledHref) return getAttribution()
    lastHandledHref = href
    const url = new URL(href)
    const touch = touchFromVisit(url, document.referrer, !referrerHandled)
    referrerHandled = true

    const prev = getAttribution()
    const site = siteRoot(window.location.hostname)
    const next: Attribution = { ft: prev?.ft, lt: prev?.lt, site }
    if (!next.ft) {
      const ft = touch ?? Object.assign(emptyTouch(url.pathname, "direct"), { source: "direct", medium: "none" })
      next.ft = ft
      next.lt = ft
    } else if (touch) {
      next.lt = touch
    }
    if (!next.lt) next.lt = next.ft
    if (JSON.stringify(next) !== JSON.stringify(prev)) write(next)
    return next
  } catch {
    return null
  }
}

// ---------------------------------------------------------------- outputs

/**
 * Compact parameters for links and forms (RFC-004 §9.2):
 * ft_src, ft_med, ft_cmp, ft_land, lt_src, lt_med, lt_cmp.
 */
export function attributionParams(a: Attribution | null = getAttribution()): Record<string, string> {
  const out: Record<string, string> = {}
  if (!a) return out
  const put = (k: string, v?: string) => {
    if (v) out[k] = v
  }
  put("ft_src", a.ft?.source)
  put("ft_med", a.ft?.medium)
  put("ft_cmp", a.ft?.campaign)
  put("ft_land", a.ft?.landing)
  put("lt_src", a.lt?.source)
  put("lt_med", a.lt?.medium)
  put("lt_cmp", a.lt?.campaign)
  return out
}

/** Hidden fields for lead forms (RFC-004 §9.5). */
export function attributionFormFields(a: Attribution | null = getAttribution()): Record<string, string> {
  const p = attributionParams(a)
  const out: Record<string, string> = {}
  const map: Record<string, string> = {
    ft_src: "ft_source", ft_med: "ft_medium", ft_cmp: "ft_campaign", ft_land: "ft_landing",
    lt_src: "lt_source", lt_med: "lt_medium", lt_cmp: "lt_campaign",
  }
  for (const [k, v] of Object.entries(p)) out[map[k]] = v
  return out
}

/**
 * The original UTM parameters to pass through: from the current URL if it has
 * any, else from the latest touch that came from UTM. Derived (referrer) values
 * are never turned into utm_* parameters.
 */
function utmPassthrough(a: Attribution | null): Record<string, string> {
  const out: Record<string, string> = {}
  try {
    const p = new URLSearchParams(window.location.search)
    for (const k of UTM_KEYS) {
      const v = p.get(k)
      if (v) out[k] = clip(v)
    }
  } catch {
    /* ignore */
  }
  if (Object.keys(out).length) return out
  const t = a?.lt?.via === "utm" ? a.lt : a?.ft?.via === "utm" ? a.ft : undefined
  if (t) {
    if (t.source) out.utm_source = t.source
    if (t.medium) out.utm_medium = t.medium
    if (t.campaign) out.utm_campaign = t.campaign
    if (t.content) out.utm_content = t.content
    if (t.term) out.utm_term = t.term
  }
  return out
}

/**
 * Add attribution to a URL (links to the Autopilot app, cross-domain links).
 * Idempotent: parameters are set, not appended. Returns the input unchanged
 * when it cannot be parsed or nothing is known.
 */
export function decorateUrl(url: string): string {
  if (!isBrowser()) return url
  try {
    const u = new URL(url, window.location.href)
    const a = getAttribution() ?? captureAttribution()
    const params = { ...utmPassthrough(a), ...attributionParams(a) }
    for (const [k, v] of Object.entries(params)) u.searchParams.set(k, v)
    return u.toString()
  } catch {
    return url
  }
}

/**
 * Decorate a link from a sister site (tikaj.com, phishgrid.com) to hunto.ai or
 * Autopilot (RFC-004 §9.1): when the link has no utm_* of its own it gets
 * `utm_source=<source>&utm_medium=referral` (and no other utm_*), otherwise its
 * own UTM is kept. The visitor's own source travels in the ft_* / lt_*
 * parameters, which this site's cookie cannot share with *.hunto.ai.
 */
export function referralUrl(url: string, source: string): string {
  if (!isBrowser()) return url
  try {
    const u = new URL(url, window.location.href)
    const hasOwnUtm = UTM_KEYS.some((k) => u.searchParams.has(k))
    const a = getAttribution() ?? captureAttribution()
    if (!hasOwnUtm) {
      u.searchParams.set("utm_source", lc(source))
      u.searchParams.set("utm_medium", "referral")
    }
    for (const [k, v] of Object.entries(attributionParams(a))) u.searchParams.set(k, v)
    return u.toString()
  } catch {
    return url
  }
}

const PII_KEY = /(e-?mail|name|phone|tel|domain|address|company)/i
const EMAIL = /[^\s@]+@[^\s@]+\.[^\s@]+/

/**
 * Push an event to `window.dataLayer` for GTM. Drops parameters whose key or
 * value could carry personal data (email, name, phone, typed domain).
 */
export function track(event: string, params: Record<string, unknown> = {}): void {
  if (!isBrowser()) return
  try {
    const clean: Record<string, string | number | boolean> = {}
    for (const [k, v] of Object.entries(params)) {
      if (PII_KEY.test(k)) continue
      if (typeof v === "string") {
        if (EMAIL.test(v)) continue
        clean[k] = clip(v)
      } else if (typeof v === "number" || typeof v === "boolean") {
        clean[k] = v
      }
    }
    const w = window as any
    w.dataLayer = w.dataLayer || []
    w.dataLayer.push({ event, ...clean })
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------- link tracking

export interface LinkTrackingOptions {
  /** Hosts whose links get `decorateUrl` at click time, e.g. ["autopilot.hunto.ai"]. */
  decorateHosts: string[]
  /**
   * Per-link decoration, run after `decorateHosts`: return the new href, or null
   * to leave the link alone (e.g. `referralUrl` for sister-site links).
   */
  decorate?: (url: URL) => string | null
  /**
   * Destination of a link for `cta_click` (signup, signin, demo, trial, contact,
   * tool, partner), or null when the link is not a CTA.
   */
  classify?: (url: URL) => string | null
}

/** Default CTA classifier: Autopilot signup/sign-in, demo, trial, contact, tools. */
export function defaultClassify(url: URL): string | null {
  const host = url.hostname.toLowerCase()
  const path = url.pathname.toLowerCase()
  if (host.startsWith("autopilot.")) return url.searchParams.get("tab") === "signup" ? "signup" : "signin"
  if (!isOwnHost(host)) return null
  if (/^\/(book-a-)?demo\/?/.test(path)) return "demo"
  if (/^\/free-trial\/?/.test(path)) return "trial"
  if (/^\/contact\/?/.test(path)) return url.searchParams.get("source")?.startsWith("partner") ? "partner" : "contact"
  if (/^\/tools\//.test(path)) return "tool"
  return null
}

/** Where on the page a CTA sits: data-cta-location, else nav / footer / hero / final-cta / inline. */
function ctaLocation(el: Element): string {
  const tagged = el.closest("[data-cta-location]")
  if (tagged) return clip(tagged.getAttribute("data-cta-location"), 40)
  if (el.closest("header, nav")) return "nav"
  if (el.closest("footer")) return "footer"
  const main = document.querySelector("main")
  const section = el.closest("section")
  if (main && section && main.contains(section)) {
    const sections = Array.from(main.querySelectorAll("section")).filter((s) => !s.parentElement?.closest("section"))
    const top = sections.find((s) => s.contains(section))
    if (top && top === sections[0]) return "hero"
    if (top && top === sections[sections.length - 1]) return "final-cta"
  }
  return "inline"
}

let installed = false

/**
 * One delegated listener for the whole document: decorates links to
 * `decorateHosts` just before navigation (click, middle-click, context menu)
 * and pushes `cta_click` for links the classifier recognises.
 */
export function installLinkTracking(opts: LinkTrackingOptions): void {
  if (!isBrowser() || installed) return
  installed = true
  const hosts = opts.decorateHosts.map((h) => h.toLowerCase())
  const classify = opts.classify ?? defaultClassify

  const handler = (ev: Event) => {
    try {
      const target = ev.target as Element | null
      const a = target?.closest?.("a[href]") as HTMLAnchorElement | null
      if (!a) return
      const url = new URL(a.href, window.location.href)
      if (hosts.includes(url.hostname.toLowerCase())) a.href = decorateUrl(url.toString())
      const custom = opts.decorate?.(new URL(a.href, window.location.href))
      if (custom) a.href = custom
      if (ev.type === "contextmenu") return
      if (ev.type === "auxclick" && (ev as MouseEvent).button !== 1) return
      const destination = classify(url)
      if (!destination) return
      const text = (a.innerText || a.textContent || "").replace(/\s+/g, " ").trim()
      track("cta_click", {
        cta_text: clip(a.getAttribute("data-cta-text") || text, 80),
        cta_location: ctaLocation(a),
        destination,
        link_url: clip(url.origin + url.pathname, MAX_PATH_LEN),
      })
    } catch {
      /* ignore */
    }
  }
  document.addEventListener("click", handler, true)
  document.addEventListener("auxclick", handler, true)
  document.addEventListener("contextmenu", handler, true)
}

/** Map a lead form's `lead_type` to the RFC-004 `form` value. */
export function leadFormFromType(leadType?: string): "contact" | "demo" | "trial" | "resource" | "partner" {
  const t = (leadType || "").toLowerCase()
  if (t.includes("trial")) return "trial"
  if (t.includes("resource")) return "resource"
  if (t.includes("partner")) return "partner"
  if (t.includes("demo")) return "demo"
  return "contact"
}
